import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, IconButton, Tooltip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, Stack, Chip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { shortAbsenceService } from '../../services/api';
import { useToast } from '../ToastProvider';
import { handleApiError } from '../Common/ApiErrorHandler';
import StatusChip from '../StatusChip';
import EmptyState from '../ui/EmptyState';
import SkeletonBlock from '../ui/SkeletonBlock';

export default function ShortAbsenceApprovalQueue({ onActionSuccess }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rejectDlg, setRejectDlg] = useState({ open: false, id: null, reason: '' });
  const toast = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await shortAbsenceService.getPending();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApprove = async (id) => {
    try {
      await shortAbsenceService.approve(id, null);
      toast.success('Request approved');
      loadData();
      onActionSuccess?.();
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleReject = async () => {
    if (!rejectDlg.reason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      await shortAbsenceService.reject(rejectDlg.id, rejectDlg.reason);
      toast.success('Request rejected');
      setRejectDlg({ open: false, id: null, reason: '' });
      loadData();
      onActionSuccess?.();
    } catch (e) {
      handleApiError(e);
    }
  };

  const slice = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <SkeletonBlock lines={5} />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!items.length) {
    return (
      <EmptyState
        icon={<CheckIcon sx={{ fontSize: 48, color: 'text.secondary' }} />}
        title="No Pending Approvals"
        description="There are no short absence requests awaiting your approval."
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Pending Approvals ({items.length})</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadData} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Monthly Total</TableCell>
              <TableCell>Note</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {slice.map((req) => (
              <TableRow key={req.id}>
                <TableCell>{req.employeeName}</TableCell>
                <TableCell>{req.date}</TableCell>
                <TableCell>{req.startTime} - {req.endTime}</TableCell>
                <TableCell>{req.durationHours}h</TableCell>
                <TableCell>
                  <Chip
                    label={`${req.monthlyTotalHours || 0}h`}
                    size="small"
                    color={req.isOverLimit ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 150 }} noWrap title={req.note}>
                    {req.note || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Approve">
                    <IconButton color="success" size="small" onClick={() => handleApprove(req.id)}>
                      <CheckIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <IconButton color="error" size="small" onClick={() => setRejectDlg({ open: true, id: req.id, reason: '' })}>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={items.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 20]}
      />

      {/* Reject Dialog */}
      <Dialog open={rejectDlg.open} onClose={() => setRejectDlg({ open: false, id: null, reason: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for rejection"
            value={rejectDlg.reason}
            onChange={(e) => setRejectDlg({ ...rejectDlg, reason: e.target.value })}
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDlg({ open: false, id: null, reason: '' })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

